// Feature pages at /features/:featureId. The words of each page live in
// public/locales/<lang>/features.json under the feature's key (the id with _ for -);
// this file keeps what is not text: icon, color, screenshots and links.
//
// Text per key, structured for AEO/GEO/SEO:
//   title, subtitle, seo_title, seo_description, keywords
//   definition (1 sentence) + summary (40-60 word answer engine opener) + description
//   benefits[], what_is (deeper "What is X?" Q&A answer)
//   how_it_works {name, description, steps[{name, text}]} -> HowTo schema (totalTime here)
//   use_cases[{title, text}] (3-5 real scenarios)
//   screenshots[{alt, caption}], one per picture in `screenshots` here, in the same order
//   comparison {title, columns[], rows[[]]} (BusyBee vs 2-3 alternatives), optional
//   faq[{question, answer}] (literal user phrasing)
// relatedFeatures are internal links; SITE_LAST_UPDATED is the visible date (recency signal).
// Every claim has to match what the app does today (checked against the code on
// SITE_LAST_UPDATED). /features/inbox and /features/mobile-app redirect elsewhere (App.js).


import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';
export const SITE_LAST_UPDATED = '2026-10-30';

export const features = {
  'task-management': {
    icon: 'LayoutDashboard', color: 'purple', totalTime: 'PT2M',
    screenshots: ['/screenshots/tasks/list.png', '/screenshots/home/main.png'],
    relatedFeatures: ['projects', 'calendar', 'recurring-tasks'],
  },
  projects: {
    icon: 'KanbanSquare', color: 'blue', totalTime: 'PT3M',
    screenshots: [
      '/screenshots/projects/list.png',
      '/screenshots/projects/kanban.png',
      '/screenshots/projects/table.png',
      '/screenshots/projects/timeline.png',
      '/screenshots/projects/calendar.png',
    ],
    relatedFeatures: ['task-management', 'team-collaboration', 'public-sharing', 'documents'],
  },
  calendar: {
    icon: 'Calendar', color: 'blue', totalTime: 'PT5M',
    screenshots: ['/screenshots/tasks/calendar.png', '/screenshots/home/main.png'],
    relatedFeatures: ['task-management', 'buffer-time', 'habits'],
  },
  'focus-time': {
    icon: 'Focus', color: 'blue', totalTime: 'PT3M',
    screenshots: ['/screenshots/focus-time/main.png'],
    relatedFeatures: ['habits', 'calendar', 'buffer-time'],
  },
  habits: {
    icon: 'Clock', color: 'yellow', totalTime: 'PT1M',
    screenshots: ['/screenshots/habits/main.png'],
    relatedFeatures: ['calendar', 'focus-time', 'recurring-tasks', 'task-management'],
  },
  documents: {
    icon: 'FileText', color: 'yellow', totalTime: 'PT2M',
    screenshots: ['/screenshots/documents/list.png', '/screenshots/documents/editor.png'],
    relatedFeatures: ['databases', 'whiteboards', 'projects'],
  },
  'team-collaboration': {
    icon: 'Users', color: 'pink', totalTime: 'PT4M',
    screenshots: ['/screenshots/projects/kanban.png', '/screenshots/settings/teams.png'],
    relatedFeatures: ['projects', 'public-sharing', 'notifications'],
  },
  'gmail-integration': {
    icon: 'Mail', color: 'blue', totalTime: 'PT3M',
    screenshots: ['/screenshots/gmail-integration/main.png'],
    relatedFeatures: ['task-management', 'webhooks', 'team-collaboration'],
  },
  analytics: {
    icon: 'BarChart3', color: 'yellow', totalTime: 'PT1M',
    screenshots: ['/screenshots/analytics/main.png'],
    relatedFeatures: ['habits', 'notifications', 'task-management'],
  },
  webhooks: {
    icon: 'Webhook', color: 'green', totalTime: 'PT5M',
    screenshots: ['/screenshots/webhooks/main.png'],
    relatedFeatures: ['mcp-server', 'task-management', 'projects'],
  },
  'buffer-time': {
    icon: 'Coffee', color: 'green', totalTime: 'PT1M',
    screenshots: ['/screenshots/buffer-time/main.png'],
    relatedFeatures: ['calendar', 'habits', 'focus-time'],
  },
  'custom-fields': {
    icon: 'Sliders', color: 'purple', totalTime: 'PT2M',
    screenshots: ['/screenshots/projects/table.png'],
    relatedFeatures: ['task-management', 'projects', 'databases'],
  },
  whiteboards: {
    icon: 'Pencil', color: 'yellow', totalTime: 'PT1M',
    screenshots: ['/screenshots/documents/whiteboard.png'],
    relatedFeatures: ['documents', 'mindmaps', 'projects'],
  },
  mindmaps: {
    icon: 'Network', color: 'purple', totalTime: 'PT2M',
    screenshots: ['/screenshots/documents/mindmap.png'],
    relatedFeatures: ['documents', 'whiteboards', 'projects'],
  },
  'recurring-tasks': {
    icon: 'Repeat', color: 'blue', totalTime: 'PT30S',
    screenshots: ['/screenshots/tasks/recurrence.png'],
    relatedFeatures: ['task-management', 'habits', 'calendar'],
  },
  'public-sharing': {
    icon: 'Globe', color: 'green', totalTime: 'PT30S',
    screenshots: ['/screenshots/share/project.png'],
    relatedFeatures: ['projects', 'team-collaboration', 'documents'],
  },
  'mcp-server': {
    icon: 'Bot', color: 'pink', totalTime: 'PT2M',
    screenshots: ['/screenshots/settings/api-mcp.png'],
    relatedFeatures: ['webhooks', 'task-management', 'documents'],
  },
  notifications: {
    icon: 'Bell', color: 'pink', totalTime: 'PT2M',
    screenshots: ['/screenshots/notifications/main.png'],
    relatedFeatures: ['task-management', 'team-collaboration', 'analytics'],
  },
  'trash-restore': {
    icon: 'Trash2', color: 'green', totalTime: 'PT15S',
    screenshots: ['/screenshots/trash/main.png'],
    relatedFeatures: ['task-management', 'projects', 'documents'],
  },
  databases: {
    icon: 'LayoutDashboard', color: 'cyan', totalTime: 'PT3M',
    screenshots: ['/screenshots/documents/database.png'],
    relatedFeatures: ['documents', 'custom-fields', 'projects'],
  },
  search: {
    icon: 'Sparkles', color: 'blue', totalTime: 'PT10S',
    screenshots: ['/screenshots/search/main.png'],
    relatedFeatures: ['task-management', 'projects', 'documents'],
  },
  languages: {
    icon: 'Sparkles', color: 'purple', totalTime: 'PT5S',
    screenshots: ['/screenshots/home/language-menu.png'],
    relatedFeatures: ['team-collaboration', 'public-sharing'],
  },
};

// The Gmail page is hidden with the in-app Gmail integration (GOOGLE_APP_INTEGRATIONS).
if (!GOOGLE_APP_INTEGRATIONS) delete features['gmail-integration'];

export const featureKey = (id) => id.replace(/-/g, '_');

const asList = (v) => (Array.isArray(v) ? v : []);

/** A feature page in the reader's language: the config above with its words (t of a useTranslation that loads 'features'). */
export const getFeature = (id, t) => {
  const config = features[id];
  if (!config) return null;
  const c = t(`features:${featureKey(id)}`, { returnObjects: true });
  const text = c && typeof c === 'object' ? c : {};
  const how = text.how_it_works || {};
  const shots = asList(text.screenshots);
  return {
    id,
    title: text.title,
    subtitle: text.subtitle,
    color: config.color,
    icon: config.icon,
    seoTitle: text.seo_title,
    seoDescription: text.seo_description,
    keywords: text.keywords,
    definition: text.definition,
    summary: text.summary,
    description: text.description,
    benefits: asList(text.benefits),
    whatIs: text.what_is,
    howItWorks: { name: how.name, description: how.description, totalTime: config.totalTime, steps: asList(how.steps) },
    useCases: asList(text.use_cases),
    screenshots: config.screenshots.map((src, i) => ({ src, ...shots[i] })),
    comparison: text.comparison,
    extendedFaq: asList(text.faq),
    relatedFeatures: config.relatedFeatures,
  };
};
